import { isJSON } from "telejson";
import { convertListValueLabel } from "../../../utils/convert-select-value";
import { isArrayWithLength } from "../../../utils/array-methods";
import i18n from "../../../config/locale/i18n";
import { isEmpty } from "lodash";

const nonSupportedInnerTypes = [
  "map(",
  "Map(",
  "object(",
  "Object(",
  "set(",
  "Set(",
  "tuple(",
  "Tuple(",
  "list(",
  "List(",
];
export const isNonSupportedTypeFound = (inputType) => {
  let foundType = nonSupportedInnerTypes.find((type) =>
    inputType.includes(type)
  );
  return !!foundType;
};

export const identifyListType = (input, depth = 0) => {
  if (depth > 1) {
    return { type: "InputField" };
  }

  const innerType = input.slice(5, -1);

  if (
    innerType === "string" ||
    innerType === "String" ||
    innerType === "number" ||
    innerType === "Number" ||
    innerType === "bool" ||
    innerType === "Bool"
  ) {
    return { type: "MultiSelectDropdownCreate" };
  }

  if (
    (innerType.startsWith("list(") || innerType.startsWith("List(")) &&
    innerType.endsWith(")")
  ) {
    // Handle nested list by recursively calling the identifyListType function
    const recursiveInnerType = identifyListType(innerType, depth + 1);
    return { type: `list(${recursiveInnerType.type})` };
  }

  if (
    (innerType.startsWith("object(") || innerType.startsWith("Object(")) &&
    innerType.endsWith(")")
  ) {
    // Handle object type with multiple key-value pairs

    const slicedInput = innerType.slice(5).slice(7);
    const isNonSupportedType = isNonSupportedTypeFound(slicedInput);

    if (isNonSupportedType) {
      return { type: "InputField" };
    } else {
      const keyValuePairs = innerType
        .slice(7, -1)
        .split(",")
        .map((pair) => pair.trim())
        .map((pair) => pair.replace(/^\{|\}$/g, ""))
        .map((pair) => {
          const parts = pair.split("=").map((part) => part.trim());
          const key = parts[0];
          const value = parts[1];
          return { key, value };
        });

      return {
        type: "ListObject",
        keyValues: keyValuePairs,
      };
    }
  }

  if (
    (innerType.startsWith("map(") || innerType.startsWith("Map(")) &&
    innerType.endsWith(")")
  ) {
    // Handle map type with multiple key-value pairs
    const slicedInput = innerType.slice(5).slice(4);
    const isNonSupportedType = isNonSupportedTypeFound(slicedInput);

    if (isNonSupportedType) {
      return { type: "InputField" };
    } else {
      const keyValuePairs = innerType
        .slice(4, -1)
        .split(",")
        .map((pair) => pair.trim())
        .map((pair) => pair.replace(/^\{|\}$/g, ""))
        .map((pair) => {
          const parts = pair.split("=").map((part) => part.trim());
          const key = parts[0];
          const value = parts[1];
          return { key, value };
        });

      return {
        type: "ListObject",
        keyValues: keyValuePairs,
      };
    }
  }

  return { type: "InputField" };
};

export const identifyObjectType = (input, depth = 0) => {
  if (depth > 1) {
    return { type: "InputField" };
  }

  if (
    (input.startsWith("object(") || input.startsWith("Object(")) &&
    input.endsWith(")")
  ) {
    const slicedInput = input.slice(7);
    const isNonSupportedType = isNonSupportedTypeFound(slicedInput);
    if (isNonSupportedType) {
      return { type: "InputField" };
    } else {
      const keyValuePairs = input
        .slice(7, -1)
        .split(",")
        .map((pair) => pair.trim())
        .map((pair) => pair.replace(/^\{|\}$/g, ""))
        .map((pair) => {
          const parts = pair.split("=").map((part) => part.trim());
          const key = parts[0];
          const value = parts[1];
          return { key, value };
        });
      return {
        type: "object",
        keyValues: keyValuePairs,
      };
    }
  }

  if (
    (input.startsWith("map(") || input.startsWith("Map(")) &&
    input.endsWith(")")
  ) {
    const slicedInput = input.slice(4);
    const isNonSupportedType = isNonSupportedTypeFound(slicedInput);

    if (isNonSupportedType) {
      return { type: "InputField" };
    } else {
      const keyValuePairs = input
        .slice(4, -1)
        .split(",")
        .map((pair) => pair.trim())
        .map((pair) => pair.replace(/^\{|\}$/g, ""))
        .map((pair) => {
          const parts = pair.split("=").map((part) => part.trim());
          const key = parts[0];
          const value = parts[1];
          return { key, value };
        });

      return {
        type: "object",
        keyValues: keyValuePairs,
      };
    }
  }

  return { type: "InputField" };
};

export const identifyTupleType = (input) => {
  const match = input.match(/\[(.*?)\]/);
  const types = match ? match[1].split(", ") : [];

  const allowedLength = types.length;
  const allowedTupleTypes = [
    "string",
    "String",
    "Number",
    "number",
    "Bool",
    "bool",
  ];

  const isTupleOfStrings = types.every((type) =>
    allowedTupleTypes.includes(type)
  );

  if (isTupleOfStrings) {
    return { type: "Tuple", allowedLength };
  } else {
    return { type: "InputField" };
  }
};

export const identifySetType = (input) => {
  const match = input.match(/set\((.*?)\)/);
  const types = match ? match[1].split(", ") : [];
  const allowedLength = types.length;

  let type = "InputField";
  const allowedTupleTypes = [
    "string",
    "String",
    "Number",
    "number",
    "Bool",
    "bool",
  ];

  if (types.length === 1 && allowedTupleTypes.includes(types[0])) {
    type = "Set";
  }

  return { type, allowedLength };
};

const listOfTypes = [
  "list(",
  "List(",
  "map(",
  "Map(",
  "object(",
  "Object(",
  "tuple(",
  "Tuple(",
  "set(",
  "Set(",
];

export const isComplexTerraformParameter = (parameterType) => {
  return listOfTypes.some((type) => parameterType.startsWith(type));
};

export const listTerraformDefaultParameterValue = (
  parameterType,
  parameterValue
) => {
  if (parameterType.startsWith("List(") || parameterType.startsWith("list(")) {
    let listType = identifyListType(parameterType);
    if (listType.type === "MultiSelectDropdownCreate") {
      return parameterValue
        ? Array.isArray(parameterValue)
          ? convertListValueLabel(parameterValue)
          : convertListValueLabel(parameterValue)[0]
        : null;
    } else if (
      [
        "list(MultiSelectDropdownCreate)",
        "List(MultiSelectDropdownCreate)",
      ].includes(listType.type)
    ) {
      return isArrayWithLength(parameterValue)
        ? parameterValue.map((_p) => {
            return convertListValueLabel(_p);
          })
        : [];
    } else if (listType.type === "ListObject") {
      return parameterValue;
    } else {
      return JSON.stringify(parameterValue);
    }
  } else if (
    parameterType.startsWith("Tuple(") ||
    parameterType.startsWith("tuple(")
  ) {
    let tupleType = identifyTupleType(parameterType);
    if (tupleType.type === "Tuple") {
      return parameterValue
        ? Array.isArray(parameterValue)
          ? convertListValueLabel(parameterValue)
          : convertListValueLabel(parameterValue)[0]
        : null;
    } else {
      return JSON.stringify(parameterValue);
    }
  } else if (
    parameterType.startsWith("Set(") ||
    parameterType.startsWith("set(")
  ) {
    let setType = identifySetType(parameterType);
    if (setType.type === "Set") {
      return parameterValue
        ? Array.isArray(parameterValue)
          ? convertListValueLabel(parameterValue)
          : convertListValueLabel(parameterValue)[0]
        : null;
    } else {
      return JSON.stringify(parameterValue);
    }
  } else if (
    parameterType.startsWith("Map(") ||
    parameterType.startsWith("map(") ||
    parameterType.startsWith("object(") ||
    parameterType.startsWith("Object(")
  ) {
    const itemType = identifyObjectType(parameterType);
    if (itemType.type === "object") {
      return parameterValue;
    } else {
      return JSON.stringify(parameterValue);
    }
  } else {
    return parameterValue;
  }
};

export const converLabelValueToTerraformFormat = (
  parameterType,
  parameterValue,
  isMultiSelect
) => {
  if (parameterType.startsWith("List(") || parameterType.startsWith("list(")) {
    let listType = identifyListType(parameterType);
    if (listType.type === "MultiSelectDropdownCreate") {
      if (isMultiSelect && Array.isArray(parameterValue)) {
        return parameterValue.map((p) => p.label || p);
      } else if (!isMultiSelect && Array.isArray(parameterValue)) {
        return parameterValue.map((p) => p.label || p).join(",");
      } else if (parameterValue === null) {
        return "";
      } else {
        const val = parameterValue && (parameterValue.value || parameterValue);
        return val && val.toString();
      }
    } else if (
      [
        "list(MultiSelectDropdownCreate)",
        "List(MultiSelectDropdownCreate)",
      ].includes(listType.type)
    ) {
      return isArrayWithLength(parameterValue)
        ? parameterValue.map((p) => {
            return isArrayWithLength(p) ? p.map((pp) => pp.label) : "";
          })
        : [];
    } else if (listType.type === "ListObject") {
      return parameterValue;
    } else {
      return parameterValue && isJSON(parameterValue)
        ? JSON.parse(parameterValue)
        : parameterValue;
    }
  } else if (
    parameterType.startsWith("Tuple(") ||
    parameterType.startsWith("tuple(")
  ) {
    let tupleType = identifyTupleType(parameterType);
    if (tupleType.type === "Tuple") {
      return isArrayWithLength(parameterValue)
        ? parameterValue.map((p) => p.label || p)
        : [];
    } else {
      return parameterValue && isJSON(parameterValue)
        ? JSON.parse(parameterValue)
        : parameterValue;
    }
  } else if (
    parameterType.startsWith("Set(") ||
    parameterType.startsWith("set(")
  ) {
    let setType = identifySetType(parameterType);

    if (setType.type === "Set") {
      return isArrayWithLength(parameterValue)
        ? parameterValue.map((p) => p.label || p)
        : [];
    } else {
      return parameterValue ? JSON.parse(parameterValue) : "";
    }
  } else if (
    parameterType.startsWith("Map(") ||
    parameterType.startsWith("map(") ||
    parameterType.startsWith("object(") ||
    parameterType.startsWith("Object(")
  ) {
    const itemType = identifyObjectType(parameterType);
    if (itemType.type === "object") {
      return parameterValue;
    } else {
      return parameterValue && isJSON(parameterValue)
        ? JSON.parse(parameterValue)
        : parameterValue;
    }
  } else {
    return parameterValue;
  }
};

export const complexErrorParametersValidation = (param, isTerraformService) => {
  if (isTerraformService && isComplexTerraformParameter(param.parameterType)) {
    if (
      param.parameterType.startsWith("tuple(") ||
      param.parameterType.startsWith("Tuple(")
    ) {
      
      const input = identifyTupleType(param.parameterType);
      const value = param.parameterValue;
      let hasError = false;

      if (input.type === "Tuple") {
        hasError = isArrayWithLength(value)
          ? value.length !== input.allowedLength
          : false;
      }

      if (input.type === "InputField") {
        hasError = isEmpty(value);
      }

      return hasError;
    }
  }

  return !param.isOptional && !param.parameterValue;
};

export const validateComplexParameter = (parameterType, parameterValue) => {
  // setting valid to true will bypass validation as true for other
  // parameters until other parameters are added
  let isValid = true,
    errorMessage = "";

  if (
    parameterType.startsWith("tuple(") ||
    parameterType.startsWith("Tuple(")
  ) {
    const input = identifyTupleType(parameterType);
    const value = parameterValue;

    if (input.type === "Tuple") {
      isValid = isArrayWithLength(value)
        ? value.length === input.allowedLength
        : false;

      if (!isValid) {
        errorMessage = i18n.t("blueprints:tupleValidationErrorMessage", {
          allowedLength: input.allowedLength,
        });
      }
    }

    if (input.type === "InputField") {
      isValid = value && value.length;
      errorMessage = "";
    }
  }

  return {
    isValid,
    errorMessage,
  };
};
